/**
 * Shared utilities for progress bar visual calculations.
 * Used by ProgressBar, StatusCell, and LoadingStatusPill components.
 */

export interface ProgressBarStyles {
  /** Visual width percentage (progress + 20%, capped at 100%) */
  visualProgress: number;
  /** Fade start percentage for gradient mask */
  fadeStartPercent: number;
}

/**
 * Calculate progress bar visual styles.
 * The progress bar shows 20% more than the actual progress to create
 * a leading edge effect, with a gradient fade in the extra portion.
 *
 * @param progress - Current progress percentage (0-100)
 * @returns Calculated style values for the progress bar
 */
export function calculateProgressBarStyles(progress: number): ProgressBarStyles {
  const currentProgress = progress ?? 0;
  // Add 20% to progress (capped at 100%) for visual leading edge
  const visualProgress = Math.min(currentProgress + 20, 100);
  // Fade starts at the actual progress point
  const fadeStartPercent = currentProgress;

  return { visualProgress, fadeStartPercent };
}

/**
 * Apply progress bar styles to an HTML element.
 * Sets CSS custom property for fade gradient and width.
 *
 * @param element - The progress bar DOM element
 * @param progress - Current progress percentage (0-100)
 */
export function applyProgressBarStyles(element: HTMLElement | null, progress: number): void {
  if (!element) return;

  const { visualProgress, fadeStartPercent } = calculateProgressBarStyles(progress);
  element.style.setProperty('--fade-start', `${fadeStartPercent}%`);
  element.style.width = `${visualProgress}%`;
}
